import React from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import LogoImg from "../../../assets/images/logo.png";
import { ReactComponent as IconHidePass } from "../../../assets/svg/eye.svg";
import { ReactComponent as IconShowPass } from "../../../assets/svg/invisible.svg";
import { forgotPasswordPath } from "../../../teacherStore";

import styles from "./TeacherResetPasswordForm.module.scss";

export interface ResetPasswordFormProps {
  updatePassword: (password: string) => void;
  isUuidExpired: boolean;
}

const validates: { [key: string]: (value: any) => string } = {};

validates["password"] = val => {
  return !val.password.trim()
    ? "この項目は入力が必須です。"
    : val.password.length < 6 || val.password.length > 32
    ? "パスワードは6〜32文字である必要があります。"
    : "";
};

validates["passwordConfirm"] = val => {
  return !val.passwordConfirm.trim()
    ? "この項目は入力が必須です。"
    : val.passwordConfirm.length < 6 || val.passwordConfirm.length > 32
    ? "パスワードは6〜32文字である必要があります。"
    : val.password !== val.passwordConfirm
    ? "パスワードが一致しません。"
    : "";
};

export const TeacherResetPasswordForm = (props: ResetPasswordFormProps) => {
  const [visible, setVisible] = React.useState({
    password: false,
    confirmPassword: false,
  });
  const [state, setState] = React.useState({
    password: "",
    passwordConfirm: "",
    isSubmitted: false,
    errorMessages: {} as { [key: string]: string },
  });

  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const errorMessages = {} as { [key: string]: string };
      Object.keys(validates).forEach(key => {
        const ret = validates[key](state);
        if (ret) {
          errorMessages[key] = ret;
        }
      });
      if (Object.keys(errorMessages).length > 0) {
        setState(s => ({ ...s, errorMessages }));
        return;
      }
      props.updatePassword(state.password);
    },
    [state, props],
  );

  const onChange = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      e.preventDefault();
      const val = e.currentTarget.value;
      const key = e.currentTarget.name;
      setState(prevState => {
        const errorMessages = {} as { [key: string]: string };
        const arg = { ...prevState, [key]: val };
        Object.keys(validates).forEach(k => {
          errorMessages[k] = validates[k](arg);
        });
        return {
          ...arg,
          errorMessages,
        };
      });
    },
    [],
  );

  return (
    <div className="d-flex">
      <div className={styles.content}>
        <p className={styles.logo}>
          <img src={LogoImg} alt="brand logo" />
        </p>
        <div className={styles.wrapper}>
          <h3 className={styles.title}>パスワード登録</h3>
          {props.isUuidExpired ? (
            <div className="d-flex flex-column justify-content-center align-items-center">
              <p className={styles.expireMessage}>有効期限が切れています。</p>
              <Link
                className={styles.resetPasswordLink}
                to={forgotPasswordPath}
              >
                こちらをクリックしてパスワードリセットをしてください。
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <div className={styles.field}>
                <label htmlFor="password">パスワード</label>
                <div
                  className={clsx(
                    styles.wrapInput,
                    state.errorMessages["password"] && styles.errorInput,
                  )}
                >
                  <input
                    type={visible.password ? "text" : "password"}
                    name="password"
                    className={
                      state.errorMessages["password"]
                        ? "is-invalid"
                        : state.password
                        ? "is-valid"
                        : ""
                    }
                    id="password"
                    value={state.password}
                    onChange={onChange}
                    onBlur={onChange}
                  />
                  <span
                    onClick={() => {
                      setVisible({
                        ...visible,
                        password: !visible.password,
                      });
                    }}
                  >
                    {visible.password ? <IconShowPass /> : <IconHidePass />}
                  </span>
                </div>
                <div className={styles.error}>
                  {state.errorMessages["password"]}
                </div>
              </div>
              <div className={styles.field}>
                <label htmlFor="passwordConfirm">確認用パスワード</label>
                <div
                  className={clsx(
                    styles.wrapInput,
                    state.errorMessages["passwordConfirm"] && styles.errorInput,
                  )}
                >
                  <input
                    type={visible.confirmPassword ? "text" : "password"}
                    id="passwordConfirm"
                    name="passwordConfirm"
                    className={
                      state.errorMessages["passwordConfirm"]
                        ? "is-invalid"
                        : state.passwordConfirm
                        ? "is-valid"
                        : ""
                    }
                    value={state.passwordConfirm}
                    onChange={onChange}
                    onBlur={onChange}
                  />
                  <span
                    onClick={() => {
                      setVisible({
                        ...visible,
                        confirmPassword: !visible.confirmPassword,
                      });
                    }}
                  >
                    {visible.confirmPassword ? (
                      <IconShowPass />
                    ) : (
                      <IconHidePass />
                    )}
                  </span>
                </div>
                <div className={styles.error}>
                  {state.errorMessages["passwordConfirm"]}
                </div>
              </div>
              <button
                className={styles.btnSubmit}
                onClick={onSubmit}
                disabled={
                  Object.keys(state.errorMessages).filter(
                    k => state.errorMessages[k],
                  ).length > 0
                }
              >
                登録
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherResetPasswordForm;
